import Phaser from "phaser";
import { GameState } from "../state/GameState";
import { LEVELS } from "../levels";
import { Palette } from "../ui/palette";
import { makeButton } from "../ui/widgets";
import { registerPortraitAnims, type PortraitKey } from "../ui/portraits";
import { RELAY, RELAY_ORDER, registerRelayAnims, type RelayKey } from "../ui/relay";

/** Kirby's reaction set for the Zoom call (see src/ui/portraits.ts). */
const KIRBY_ANIMS: PortraitKey[] = [
  "kirby-blink", "kirby-talk", "kirby-laugh", "kirby-cheer",
  "kirby-clap", "kirby-grimace", "kirby-drink", "kirby-wave",
];

const STAGE_H = 150; // on-screen height of the relay runner

/**
 * The Kirby Relay (stubbed). One short phase each for Dan → Lucas → Andrew,
 * back to back, with a high-five tag-in between. The persuasion meter lives in
 * Kirby's Zoom HUD and is COSMETIC for now (always ends convinced), per the
 * design's open question - promoting it to "can fail" is a one-line change.
 */
export class FinaleScene extends Phaser.Scene {
  private meter = 0.15; // 0..1
  private meterBar!: Phaser.GameObjects.Rectangle;
  private kirby!: Phaser.GameObjects.Sprite;
  private kirbyMood!: Phaser.GameObjects.Text;
  private kirbyQuote!: Phaser.GameObjects.Text;
  private stage!: Phaser.GameObjects.Sprite; // the relay runner on stage
  private phaseIdx = 0;
  private presses = 0;
  private accepting = false;
  private prompt!: Phaser.GameObjects.Text;
  private tally!: Phaser.GameObjects.Text;

  constructor() {
    super("Finale");
  }

  create() {
    const { width } = this.scale;
    this.meter = 0.15;
    this.phaseIdx = 0;

    // --- Kirby's Zoom HUD (diegetic persuasion meter) ---
    this.add.rectangle(width / 2, 60, width - 80, 92, Palette.panel, 1).setStrokeStyle(2, Palette.kirby);
    registerPortraitAnims(this, KIRBY_ANIMS);
    this.kirby = this.add.sprite(90, 60, "kirby-blink").setScale(0.6).play("kirby-blink");
    this.kirbyMood = this.add.text(150, 40, "🙂 Kirby", { fontFamily: "monospace", fontSize: "16px", color: Palette.text });
    this.kirbyQuote = this.add.text(150, 64, "“Show me what you can do.”", { fontFamily: "monospace", fontSize: "12px", color: Palette.textDim });

    this.add.text(width - 340, 44, "PERSUASION", { fontFamily: "monospace", fontSize: "12px", color: Palette.textDim });
    this.add.rectangle(width - 340, 66, 260, 18, 0x23262e, 1).setOrigin(0, 0.5);
    this.meterBar = this.add.rectangle(width - 340, 66, 260 * this.meter, 18, Palette.kirby, 1).setOrigin(0, 0.5);

    this.prompt = this.add
      .text(width / 2, 250, "", { fontFamily: "monospace", fontSize: "22px", color: Palette.text, align: "center" })
      .setOrigin(0.5);
    this.tally = this.add
      .text(width / 2, 300, "", { fontFamily: "monospace", fontSize: "18px", color: Palette.textDim })
      .setOrigin(0.5);

    this.input.keyboard!.on("keydown-SPACE", () => {
      if (this.accepting) {
        this.presses++;
        this.tally.setText(`${this.presses}`);
      }
    });
    this.input.keyboard!.on("keydown-ESC", () => this.scene.start("LevelSelect"));

    // The relay runner: one sprite reused across all three, standing to the right
    // of the prompt with feet on a common ground line.
    registerRelayAnims(this);
    this.stage = this.add.sprite(width / 2 + 250, 470, "andrew-tag-in").setOrigin(0.5, 1).setVisible(false);

    this.time.delayedCall(600, () => this.enterPhase());
  }

  /** Bring the current relay runner on stage: play their tag-in, then start the
   *  phase. Dan starts the relay with no tag-in, so he just takes his mark. */
  private enterPhase() {
    const char = RELAY_ORDER[this.phaseIdx];
    this.prompt.setText("");
    this.tally.setText("");
    if (char.tagIn) {
      this.setKirby("kirby-talk", "🙂 Kirby (watching)", "“Next up…”");
      this.playTag(char.tagIn, () => this.runPhase());
    } else {
      // Dan: no tag-in. Take the stage on the first frame of his run.
      this.showStage(char.tagOut!, 0);
      this.runPhase();
    }
  }

  /** Point the stage sprite at a tag texture, sized to STAGE_H, on a given frame. */
  private showStage(key: RelayKey, frame = 0) {
    this.stage.setTexture(key).setFrame(frame).setScale(STAGE_H / RELAY[key].fh).setVisible(true).setAlpha(1);
  }

  /** Play a one-shot tag animation on the stage sprite; run `done` on completion. */
  private playTag(key: RelayKey, done: () => void) {
    this.showStage(key);
    this.stage.play({ key, repeat: 0 });
    this.stage.once(Phaser.Animations.Events.ANIMATION_COMPLETE, done);
  }

  /** Swap Kirby's reaction animation + his Zoom-panel label/quote in one call. */
  private setKirby(anim: PortraitKey, mood: string, quote?: string) {
    this.kirby.play(anim, true);
    this.kirbyMood.setText(mood);
    if (quote) this.kirbyQuote.setText(quote);
  }

  private runPhase() {
    const lvl = LEVELS[this.phaseIdx];
    const verbs = ["grind - MASH SPACE!", "solve - MASH SPACE!", "sprint - MASH SPACE!"];
    this.presses = 0;
    this.accepting = true;
    this.setKirby("kirby-talk", "🙂 Kirby (watching)", "“Impress me.”");
    this.tally.setText("0");
    this.prompt.setText(`Relay ${this.phaseIdx + 1}/3 · ${lvl.title}\n${verbs[this.phaseIdx]}`);
    this.cameras.main.setBackgroundColor(lvl.accent);
    this.cameras.main.setBackgroundColor(Palette.bg);

    this.time.delayedCall(3000, () => this.endPhase());
  }

  private endPhase() {
    this.accepting = false;
    const gained = Phaser.Math.Clamp(this.presses / 25, 0.08, 0.33); // cosmetic: always some gain
    this.meter = Phaser.Math.Clamp(this.meter + gained, 0, 1);
    this.tweens.add({
      targets: this.meterBar,
      width: 260 * this.meter,
      duration: 400,
    });
    if (this.presses > 12) this.setKirby("kirby-cheer", "😄 Kirby (impressed)", "“Now that's what I'm talking about!”");
    else if (this.presses < 5) this.setKirby("kirby-grimace", "😕 Kirby (unconvinced)", "“…is that all?”");
    else this.setKirby("kirby-blink", "🙂 Kirby (curious)", "“Keep going.”");

    const char = RELAY_ORDER[this.phaseIdx];
    if (this.phaseIdx < 2) {
      // The current runner tags out; when they clear, the next runner tags in.
      this.prompt.setText("");
      this.tally.setText("");
      this.setKirby("kirby-drink", "☕ Kirby", "“Nice. Tag out!”");
      this.playTag(char.tagOut!, () => {
        this.stage.setVisible(false);
        this.phaseIdx++;
        this.enterPhase();
      });
    } else {
      this.finish(); // Andrew is last: no tag-out, he stays on stage
    }
  }

  private finish() {
    const { width, height } = this.scale;
    this.meter = 1;
    this.tweens.add({ targets: this.meterBar, width: 260, duration: 400 });
    this.setKirby("kirby-clap", "🤝 Kirby (convinced!)", "“Welcome to the US market.”");
    this.prompt.setText("Kirby is convinced.\nThe US market unlocks - money floods in.");
    this.tally.setText("");
    GameState.setFinaleCleared();
    makeButton(this, width / 2, height - 90, "To Mallorca  ▶", () => this.scene.start("Ending"), {
      width: 260,
      fill: Palette.kirby,
    });
  }
}
